import { AlertTitle, ColoredAlert } from '@bpinternal/ui-kit-next'
import { setTag } from '@sentry/react'
import { Outlet, createFileRoute, redirect, useMatchRoute } from '@tanstack/react-router'
import { ActiveTrialCallout } from '~/features/billing/components/ActiveTrialCallout'
import { useCurrentRouteId, useIsAuthorized } from '~/hooks'
import { FileRouteTypes } from '~/routeTree.gen'
import { getQueryOptions } from '~/services'
import { workspacesQueryOptions } from '~/queries'
import { getWorkspacePreference } from '~/services/preferences/workspacePreferences'
import { config } from '~/shared'
import { useAppStore, useLocalStore } from '~/stores'
import { initSupportWebchat } from '~/utils/support-webchat'
import { BannerPortal, Boundary } from '../../components'
import { LegacyPricingCallout } from '../../features/billing/components/LegacyPricingCallout'
import { UnpaidInvoiceCallout } from '../../features/billing/components/UnpaidInvoiceCallout'
import { WorkspaceUsageCallout } from '../../features/billing/components/WorkspaceUsageCallout'
import { isAuthorized } from '../../features/users/services'
import { Permission } from '../../features/users/types'

const BANNER_DISABLED_PAGES: FileRouteTypes['to'][] = [
  '/workspaces/$workspaceId/bots/$botId/new-bot',
  '/workspaces/$workspaceId/bots/$botId/new-chat',
]

export const Route = createFileRoute('/workspaces/$workspaceId')({
  beforeLoad: async ({ context, params }) => {
    initSupportWebchat(params.workspaceId)
    const members = await context.queryClient.ensureQueryData(
      getQueryOptions('workspaces_/$workspaceId_/members', { workspaceId: params.workspaceId })
    )
    const workspaceMember = members?.find((member) => member.userId === context.user.id)
    const isUserAuthorized = (permission: Permission): boolean | undefined => {
      if (!workspaceMember) {
        return undefined
      }

      return isAuthorized({ permission, role: workspaceMember?.role })
    }
    return { isUserAuthorized }
  },
  loader: async ({ context, params, location }) => {
    const { setActiveWorkspace } = useAppStore.getState()
    const { setLastActiveWorkspaceId } = useLocalStore.getState()
    const workspace = await context.queryClient.fetchQuery(
      getQueryOptions('workspaces_/$workspaceId_', { workspaceId: params.workspaceId })
    )
    setActiveWorkspace(workspace)
    setLastActiveWorkspaceId(workspace.id)

    const [onboardingCompleted, workspaces] = await Promise.all([
      getWorkspacePreference({ path: 'onBoardingCompleted' }, params.workspaceId),
      context.queryClient.ensureQueryData(workspacesQueryOptions()),
    ])

    // Check if workspace was created within the last 30 minutes
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000)
    const workspaceCreatedRecently = workspace.createdAt ? new Date(workspace.createdAt) > thirtyMinutesAgo : false

    // Skip onboarding if user already has other workspaces (not a new user)
    const isFirstWorkspace = workspaces.length <= 1

    const shouldRedirectToOnboarding =
      !location.pathname.endsWith('/create-bot') &&
      !location.pathname.endsWith('/onboarding') &&
      !location.pathname.endsWith('/checkout-trigger') &&
      !onboardingCompleted &&
      workspaceCreatedRecently &&
      isFirstWorkspace

    if (shouldRedirectToOnboarding) {
      console.log('Redirecting to onboarding because onboarding is not completed and workspace is new', {
        workspaceId: params.workspaceId,
        pathname: location.pathname,
        onboardingCompleted,
        workspaceCreatedAt: workspace.createdAt,
        workspaceCreatedRecently,
      })

      throw redirect({
        to: '/workspaces/$workspaceId/onboarding',
        params: { workspaceId: params.workspaceId },
      })
    }

    return {}
  },
  component: Component,
})

function Component() {
  const { workspaceId } = Route.useParams()
  const { user } = Route.useRouteContext()
  const hasAccess = useIsAuthorized({ workspaceId, userId: user.id })
  const matchRoute = useMatchRoute()
  const routeId = useCurrentRouteId()

  setTag('workspaceId', workspaceId)

  const isBannerEnabled = !BANNER_DISABLED_PAGES.some((route) => matchRoute({ to: route }))

  function isWebchatLocation(): boolean {
    if (!routeId) return false

    return (
      routeId.includes('/webchat/v2/identity') ||
      routeId.includes('/webchat/v2/appearance') ||
      routeId.includes('/webchat/v2/deploy-settings') ||
      routeId.includes('/webchat/v2/features') ||
      routeId.includes('/webchat/v2/') ||
      routeId.includes('/webchat')
    )
  }

  return (
    <>
      {isBannerEnabled && (
        <BannerPortal>
          <Boundary loader={null} onError={() => null}>
            {hasAccess('workspace.manageBilling') && <ActiveTrialCallout workspaceId={workspaceId} />}
            {hasAccess('workspace.manageBilling') && <UnpaidInvoiceCallout workspaceId={workspaceId} />}
            <WorkspaceUsageCallout workspaceId={workspaceId} className={isWebchatLocation() ? 'xl:w-[47.5%]' : ''} />
            <LegacyPricingCallout workspaceId={workspaceId} className={isWebchatLocation() ? 'xl:w-[47.5%]' : ''} />
          </Boundary>
          {!user.emailVerified && (
            <ColoredAlert color="blue" className={isWebchatLocation() ? 'xl:w-[47.5%]' : ''}>
              <AlertTitle>
                Your email address is not verified. Email verification will be enforced soon.{' '}
                <a className="underline" target="_blank" rel="noreferrer" href={`${config.ssoBaseUrl}/verification`}>
                  Verify now
                </a>
              </AlertTitle>
            </ColoredAlert>
          )}
        </BannerPortal>
      )}
      <Outlet />
    </>
  )
}
